import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Flame, Star } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PLANS } from "@/lib/plans";
import { WaitlistDialog } from "@/components/WaitlistDialog";

function FoundingBanner() {
  return (
    <div className="mb-10 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl border border-orange-500/30 bg-orange-500/8 px-6 py-4 text-sm">
      <div className="flex items-center gap-2 font-semibold text-orange-400">
        <Flame className="w-4 h-4" />
        Founding Member Pricing — First 100 total members only
      </div>
      <div className="h-4 w-px bg-orange-500/30 hidden sm:block" />
      <div className="font-semibold text-white">
        Lock In Your Founding Price
      </div>
    </div>
  );
}

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "Launch, Scale, Dominate — AdKavi" },
      {
        name: "description",
        content:
          "First 100 total founding members lock in their chosen plan. 14-day free trial. Cancel anytime.",
      },
      { property: "og:title", content: "Pricing — AdKavi" },
      {
        property: "og:description",
        content: "Founding member pricing — only 100 total spots across all plans. Launch $149/mo, Scale $297/mo, Dominate $497/mo. 14-day free trial.",
      },
      { property: "og:url", content: "https://adkavi.ai/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.ai/pricing" }],
  }),
});

function PricingPage() {
  const creators = PLANS.filter((p) => p.category === "creator");
  const agencies = PLANS.filter((p) => p.category === "agency");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Launch, Scale, <span className="text-gradient-orange">Dominate.</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              All plans include a 14-day free trial. Cancel anytime. Card required at signup.
            </p>
          </div>

          <FoundingBanner />

          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-6 text-center">
            For Businesses & Creators
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20 items-start max-w-5xl mx-auto">
            {creators.map((p) => <PlanCard key={p.priceId} plan={p} />)}
          </div>

          <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-6 text-center">
            For Agencies
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start max-w-5xl mx-auto">
            {agencies.map((p) => <PlanCard key={p.priceId} plan={p} />)}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Agency plans include a white-label dashboard option.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function PlanCard({ plan }: { plan: (typeof PLANS)[number] }) {
  const popular = plan.popular;
  return (
    <div
      className={`relative p-6 rounded-3xl border flex flex-col ${
        popular
          ? "bg-gradient-to-br from-primary/15 to-secondary/10 border-primary glow-orange"
          : "bg-card border-border"
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          <Star className="w-3 h-3" fill="currentColor" /> Most Popular
        </div>
      )}
      <h3 className="font-bold text-lg mb-1">{plan.name}</h3>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-4xl font-bold">${plan.price.toLocaleString()}</span>
        <span className="text-sm text-muted-foreground">/mo</span>
        {plan.originalPrice && (
          <span className="text-sm text-muted-foreground line-through">${plan.originalPrice.toLocaleString()}</span>
        )}
      </div>
      {plan.foundingOffer ? (
        <p className="text-xs text-orange-400 font-medium mb-3">
          Limited to the first 100 total founding members
        </p>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">14-day free trial</p>
      )}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2 text-sm">
            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground uppercase">{f}</span>
          </li>
        ))}
      </ul>
      <WaitlistDialog plan={{ name: plan.name, price: plan.price }}>
        <button
          style={{ backgroundColor: "#f97316", color: "#ffffff" }}
          className="block w-full text-center rounded-full py-2.5 text-sm font-semibold hover:brightness-110 transition"
        >
          {plan.foundingOffer ? "Join Waitlist — Lock In This Price" : "Join Waitlist"}
        </button>
      </WaitlistDialog>
    </div>
  );
}
